import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import UsageBilling from './UsageBilling';
import BillingService from '../../services/api/billing';
import { API_RESPONSE } from '../../settings/shared/constants';
import '../../assets/css/billing.css';

/* ─────────────────────────────────────────────
   Design tokens
───────────────────────────────────────────── */
const T = {
  navy: '#0f172a',
  accent: '#3b82f6',
  border: 'rgba(0,0,0,0.07)',
  borderMid: 'rgba(0,0,0,0.12)',
  bg: '#f8fafc',
  surface: '#ffffff',
  textPrimary: '#0f172a',
  textSecondary: '#64748b',
  textMuted: '#94a3b8',
  fontSans: "'DM Sans','Segoe UI',sans-serif",
};

/* ─────────────────────────────────────────────
   Icons
───────────────────────────────────────────── */
const IcArrowLeft = () => (
  <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
    <polyline points="9,2 4,7 9,12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const IcBilling = () => (
  <svg width="16" height="16" viewBox="0 0 18 18" fill="none">
    <rect x="1.5" y="4" width="15" height="11" rx="2" stroke="currentColor" strokeWidth="1.3" />
    <path d="M1.5 8h15" stroke="currentColor" strokeWidth="1.3" />
    <rect x="3.5" y="11" width="4" height="2" rx="0.5" fill="currentColor" />
  </svg>
);

const IcSpin = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" style={{ animation: 'bpSpin 0.8s linear infinite' }}>
    <circle cx="9" cy="9" r="7" stroke="rgba(0,0,0,0.1)" strokeWidth="2" />
    <path d="M9 2a7 7 0 0 1 7 7" stroke={T.accent} strokeWidth="2" strokeLinecap="round" />
  </svg>
);

/* ─────────────────────────────────────────────
   BillingPage
───────────────────────────────────────────── */
const BillingPage = () => {
  const navigate = useNavigate();
  const [billingData, setBillingData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const accountId = localStorage.getItem('accountId');
    if (!accountId) {
      setLoading(false);
      return;
    }
    BillingService.getAccountCreditUsageSummary()
      .then((response) => {
        if (response.status === API_RESPONSE.SUCCESS) {
          setBillingData(response);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const handleBackToHome = () => navigate('/apps');

  /* ── Loading state ── */
  if (loading) {
    return (
      <>
        <style>{`@keyframes bpSpin { to { transform: rotate(360deg); } }`}</style>
        <div
          style={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            background: T.bg,
            gap: 12,
            fontFamily: T.fontSans,
          }}
        >
          <IcSpin />
          <span style={{ fontSize: 13, color: T.textMuted }}>Loading billing info…</span>
        </div>
      </>
    );
  }

  /* ── Main render ── */
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&display=swap');
        @keyframes bpSpin { to { transform: rotate(360deg); } }
        @keyframes bpFadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
        .bp-root * { box-sizing: border-box; }
        .bp-back:hover { background: rgba(0,0,0,0.05) !important; }
      `}</style>

      <div
        className="bp-root"
        style={{
          minHeight: '100vh',
          background: T.bg,
          fontFamily: T.fontSans,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* ── Topbar ── */}
        <div
          style={{
            background: T.surface,
            borderBottom: `0.5px solid ${T.border}`,
            height: 56,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 28px',
            position: 'sticky',
            top: 0,
            zIndex: 10,
            flexShrink: 0,
          }}
        >
          {/* Left: back + title */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <button
              className="bp-back"
              onClick={handleBackToHome}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: T.textSecondary,
                fontSize: 13,
                fontFamily: T.fontSans,
                borderRadius: 7,
                padding: '5px 8px',
                transition: 'background 0.14s',
              }}
            >
              <IcArrowLeft /> Back to home
            </button>

            <div style={{ width: 1, height: 18, background: T.border }} />

            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 8,
                  background: T.navy,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  flexShrink: 0,
                }}
              >
                <IcBilling />
              </div>
              <span style={{ fontSize: 14, fontWeight: 600, color: T.textPrimary }}>
                Usage &amp; Billing
              </span>
            </div>
          </div>          
        </div>

        {/* ── Content ── */}
        <div
          style={{
            flex: 1,
            padding: '28px 28px 48px',
            animation: 'bpFadeIn 0.2s ease',
          }}
        >
          <UsageBilling billingData={billingData} onClose={handleBackToHome} />
        </div>
      </div>
    </>
  );
};

export default BillingPage;